import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ShoppingBag, CreditCard, AlertCircle, Users, FileSpreadsheet, Phone, CheckSquare, MessageCircle, Calendar, Scale, ArrowUpDown, History, Moon, Download, UserCheck, CalendarDays, Settings, Shield, Megaphone, TrendingUp, ChevronRight, Send, RefreshCw, Plus, X, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  EMPLOYEES, VEHICLES, EXPENSES,
  DIESEL_LOG, CHECKLIST_TASKS, CHAT_MESSAGES,
} from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtINR(n) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n);
}

function PageHeader({ icon: Icon, section, title, subtitle }) {
  return (
    <div className="mb-5">
      <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5">
        <Icon size={11} /> {section}
      </div>
      <h1 className="font-black text-3xl tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function SimpleTable({ headers, rows, className = '' }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {headers.map(h => (
                <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                {row.map((cell, j) => (
                  <td key={j} className="px-4 py-3 text-[13px]">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{rows.length} entries</div>
    </div>
  );
}

/* ─── Purchase Logs ─────────────────────────────────────────── */
export function PurchaseLogs() {
  const { authHeader } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    axios.get(`${API}/purchases`, { headers: authHeader() })
      .then(r => setPurchases(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Flatten: one row per item within each purchase
  const rows = purchases.flatMap(p =>
    (p.items && p.items.length > 0 ? p.items : [{}]).map((item, idx) => [
      idx === 0 ? p.date : '',
      idx === 0 ? <span className="font-semibold">{p.challan_no}</span> : '',
      idx === 0 ? p.vendor_name : '',
      idx === 0 ? p.godown : '',
      item.brand  || '—',
      item.size   || '—',
      item.qty    ?? '—',
      item.kg_per_cyl ?? '—',
      idx === 0 ? <span className="font-semibold">{fmtINR(p.total_amount)}</span> : '',
    ])
  );

  return (
    <div className="px-6 py-6">
      <PageHeader icon={ShoppingBag} section="Stock" title="Purchase Logs" subtitle="All gas purchases from vendors" />
      {loading
        ? <div className="flex justify-center py-10"><RefreshCw size={18} className="animate-spin text-gray-400" /></div>
        : <SimpleTable headers={['Date','Challan','Vendor','Godown','Brand','Size','Qty','KG/Cyl','Amount ₹']} rows={rows} />
      }
    </div>
  );
}

/* ─── Payments Log ─────────────────────────────────────────── */
export function PaymentsLog() {
  const { authHeader } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    axios.get(`${API}/payments`, { headers: authHeader() })
      .then(r => setPayments(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const rows = payments.map(r => {
    const type = (r.payment_type || '').toUpperCase();
    return [
      r.date,
      <span className="font-medium">{r.party_name}</span>,
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${type === 'RECEIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{type}</span>,
      r.mode,
      <span className={`font-semibold ${type === 'RECEIVE' ? 'text-green-700' : 'text-red-700'}`}>{fmtINR(r.amount)}</span>,
      <span className="text-gray-500">{r.note}</span>,
    ];
  });

  return (
    <div className="px-6 py-6">
      <PageHeader icon={CreditCard} section="Stock" title="Payments Log" subtitle="All payment transactions" />
      {loading
        ? <div className="flex justify-center py-10"><RefreshCw size={18} className="animate-spin text-gray-400" /></div>
        : <SimpleTable headers={['Date','Party','Type','Mode','Amount ₹','Note']} rows={rows} />
      }
    </div>
  );
}

/* ─── Pending Payments ─────────────────────────────────────── */
export function PendingPayments() {
  const { authHeader } = useAuth();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/pending-payments`, { headers: authHeader() })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalOutstanding = data.reduce((s, r) => s + (r.outstanding > 0 ? r.outstanding : 0), 0);

  return (
    <div className="px-6 py-6">
      <PageHeader icon={AlertCircle} section="Pending" title="Pending Payments" subtitle="Outstanding customer balances — billed minus received" />

      {totalOutstanding > 0 && (
        <div className="mb-5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <span className="text-sm text-amber-800 font-medium">Total outstanding from customers</span>
          <span className="font-black text-amber-900 text-lg">{fmtINR(totalOutstanding)}</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Customer','Bills','Total Billed ₹','Total Received ₹','Outstanding ₹','Last Sale'].map(h => (
                  <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-[13px]">No customers with sales yet.</td></tr>
              ) : data.map(r => (
                <tr key={r.customer_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-[13px] font-medium text-gray-900">{r.customer_name}</div>
                    <div className="text-[11px] text-gray-400">{r.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-600">{r.bills}</td>
                  <td className="px-4 py-3 text-[13px] font-semibold text-gray-800">{fmtINR(r.total_billed)}</td>
                  <td className="px-4 py-3 text-[13px] text-green-700 font-semibold">{fmtINR(r.total_paid)}</td>
                  <td className="px-4 py-3">
                    <span className={`font-bold text-[13px] ${r.outstanding > 0 ? 'text-red-700' : r.outstanding < 0 ? 'text-green-700' : 'text-gray-400'}`}>
                      {r.outstanding > 0 ? fmtINR(r.outstanding) : r.outstanding < 0 ? `Overpaid ${fmtINR(-r.outstanding)}` : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-500">{r.last_sale || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
          {data.length} customers with transactions
        </div>
      </div>
    </div>
  );
}

/* ─── Customers & Vendors ──────────────────────────────────── */
const BLANK_FORM = { name: '', phone: '', customer_type: 'customer', current_rate: '', address: '', gstin: '' };

export function CustomersVendors() {
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab]           = useState('customers');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [form, setForm]         = useState(BLANK_FORM);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');

  const load = () => {
    setLoading(true);
    axios.get(`${API}/customers`, { headers: authHeader() })
      .then(r => setCustomers(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const res = await axios.post(`${API}/customers`, {
        name: form.name, phone: form.phone,
        customer_type: form.customer_type,
        current_rate: +form.current_rate || 0,
        address: form.address, gstin: form.gstin,
      }, { headers: authHeader() });
      setCustomers(c => [...c, res.data]);
      setShowAdd(false);
      setForm(BLANK_FORM);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error adding customer.');
    } finally {
      setSaving(false);
    }
  };

  const visible = customers.filter(c => tab === 'customers' ? c.customer_type !== 'vendor' : c.customer_type === 'vendor');

  const inp2 = 'px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 w-full bg-white';
  const sel2 = inp2;

  return (
    <div className="px-6 py-6">
      <div className="flex items-start justify-between mb-5 gap-4">
        <PageHeader icon={Users} section="Parties" title="Customers & Vendors" subtitle="All parties in the system" />
        <button onClick={() => setShowAdd(true)} data-testid="add-customer-btn"
          className="btn-primary flex items-center gap-1.5 text-sm whitespace-nowrap self-start mt-6">
          <Plus size={13} /> Add {tab === 'customers' ? 'Customer' : 'Vendor'}
        </button>
      </div>

      {/* Add Customer Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-black text-lg tracking-tight">Add {form.customer_type === 'vendor' ? 'Vendor' : 'Customer'}</h2>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-700 transition-colors">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="small-label block mb-1.5">Name <span className="text-red-500">*</span></label>
                  <input className={inp2} type="text" value={form.name} onChange={e => setF('name', e.target.value)} placeholder="Full name" required data-testid="customer-name-input" />
                </div>
                <div>
                  <label className="small-label block mb-1.5">Phone</label>
                  <input className={inp2} type="tel" value={form.phone} onChange={e => setF('phone', e.target.value)} placeholder="9876543210" />
                </div>
                <div>
                  <label className="small-label block mb-1.5">Type</label>
                  <select className={sel2} value={form.customer_type} onChange={e => setF('customer_type', e.target.value)}>
                    <option value="customer">Customer</option>
                    <option value="vendor">Vendor</option>
                  </select>
                </div>
                <div>
                  <label className="small-label block mb-1.5">Rate ₹/kg</label>
                  <input className={inp2} type="number" min="0" step="0.01" value={form.current_rate} onChange={e => setF('current_rate', e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <label className="small-label block mb-1.5">GSTIN</label>
                  <input className={inp2} type="text" value={form.gstin} onChange={e => setF('gstin', e.target.value)} placeholder="Optional" />
                </div>
                <div className="col-span-2">
                  <label className="small-label block mb-1.5">Address</label>
                  <input className={inp2} type="text" value={form.address} onChange={e => setF('address', e.target.value)} placeholder="Full address" />
                </div>
              </div>
              {msg && <p className="text-red-600 text-sm">{msg}</p>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2 text-sm" data-testid="save-customer-btn">
                  {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        {['customers','vendors'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-md text-[13px] font-semibold capitalize transition-all ${tab === t ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><RefreshCw size={18} className="animate-spin text-gray-400" /></div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['Name','Phone','Type','Rate ₹/kg','Address','Action'].map(h => (
                    <th key={h} className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-400 text-[13px]">
                      No {tab} yet.{' '}
                      <button onClick={() => setShowAdd(true)} className="text-blue-600 hover:underline">Add one →</button>
                    </td>
                  </tr>
                ) : visible.map(c => (
                  <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3"><div className="text-[13px] font-medium text-gray-900">{c.name}</div></td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{c.phone || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase">{c.customer_type || 'customer'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-[13px]">{c.current_rate > 0 ? `₹${c.current_rate.toFixed(2)}` : '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{c.address || '—'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/customers/${c.id}/ledger`)}
                        className="text-[12px] text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        data-testid={`view-ledger-btn-${c.id}`}
                      >
                        Ledger →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{visible.length} entries</div>
        </div>
      )}
    </div>
  );
}

/* ─── Rate Analysis ──────────────────────────────────────────── */
export function RateAnalysis() {
  const { authHeader, user } = useAuth();
  const now = new Date();
  const currMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const [month,    setMonth]    = useState(currMonth);
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editId,   setEditId]   = useState(null);
  const [editRate, setEditRate] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [expanded, setExpanded] = useState(null); // customer_id with history open

  const load = () => {
    setLoading(true);
    axios.get(`${API}/rate-analysis?month=${month}`, { headers: authHeader() })
      .then(r => setData(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, [month]);

  const startEdit = (c) => { setEditId(c.customer_id); setEditRate(String(c.current_rate || '')); };
  const cancelEdit = ()  => { setEditId(null); setEditRate(''); };

  const saveRate = async (cust) => {
    const rate = parseFloat(editRate);
    if (isNaN(rate) || rate < 0) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/rate-history`, { customer_id: cust.customer_id, rate, month }, { headers: authHeader() });
      setData(d => d.map(c => c.customer_id === cust.customer_id ? {
        ...c, current_rate: rate, rate_changed: true,
        last_changed_by: r.data.changed_by, last_changed_at: r.data.changed_at,
        history: [r.data, ...(c.history || []).slice(0, 4)],
      } : c));
      setEditId(null);
    } catch { alert('Failed to save rate'); }
    finally { setSaving(false); }
  };

  const fmtRate = n => n > 0 ? `₹${n.toFixed(2)}` : <span className="text-gray-400">Not set</span>;
  const fmtDiff = (curr, prev) => {
    if (!curr || !prev || curr === prev) return null;
    const d = curr - prev; const pct = ((d / prev) * 100).toFixed(1);
    return <span className={`text-[11px] font-bold ml-1 ${d > 0 ? 'text-red-500' : 'text-green-600'}`}>{d > 0 ? `+₹${d.toFixed(2)}` : `-₹${Math.abs(d).toFixed(2)}`} ({d > 0 ? '+' : ''}{pct}%)</span>;
  };

  const fmtDT = iso => iso ? new Date(iso).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';

  return (
    <div className="px-6 py-6" data-testid="rate-analysis-page">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5"><TrendingUp size={11} /> Reports</div>
          <h1 className="font-black text-3xl tracking-tight">Rate Analysis</h1>
          <p className="text-sm text-gray-500 mt-1">Monthly rates per customer — edit current month rate here</p>
        </div>
        <input type="month" value={month} onChange={e => { setMonth(e.target.value); setEditId(null); }}
          className="self-start mt-6 px-3 py-2 border border-gray-200 rounded-md text-[13px] focus:outline-none focus:border-gray-400 bg-white" />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left small-label text-gray-500 min-w-[180px]">Customer</th>
                <th className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">Prev Month Rate</th>
                <th className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">Current Month Rate</th>
                <th className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">Last Changed By</th>
                <th className="px-4 py-3 text-left small-label text-gray-500 whitespace-nowrap">Changed At</th>
                <th className="px-4 py-3 text-left small-label text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10"><RefreshCw size={16} className="animate-spin text-gray-400 mx-auto" /></td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-[13px]">No customers. Add them via Customers &amp; Vendors.</td></tr>
              ) : data.map(c => (
                <React.Fragment key={c.customer_id}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[13px] text-gray-900">{c.customer_name}</div>
                      {c.phone && <div className="text-xs text-gray-400">{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{fmtRate(c.prev_rate)}</td>
                    <td className="px-4 py-3">
                      {editId === c.customer_id ? (
                        <input autoFocus type="number" min="0" step="0.01"
                          className="w-28 px-2 py-1 border border-blue-400 rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-blue-400"
                          value={editRate} onChange={e => setEditRate(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveRate(c); if (e.key === 'Escape') cancelEdit(); }}
                          data-testid={`rate-input-${c.customer_id}`}
                        />
                      ) : (
                        <span className="font-semibold text-[13px] text-gray-900">
                          {fmtRate(c.current_rate)}{fmtDiff(c.current_rate, c.prev_rate)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{c.last_changed_by || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">{fmtDT(c.last_changed_at)}</td>
                    <td className="px-4 py-3">
                      {editId === c.customer_id ? (
                        <div className="flex items-center gap-2">
                          <button onClick={() => saveRate(c)} disabled={saving}
                            className="text-[12px] text-green-700 hover:text-green-900 font-medium flex items-center gap-1"
                            data-testid={`save-rate-btn-${c.customer_id}`}>
                            {saving ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />} Save
                          </button>
                          <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-700"><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button onClick={() => startEdit(c)}
                            className="text-[12px] text-blue-600 hover:text-blue-800 font-medium"
                            data-testid={`edit-rate-btn-${c.customer_id}`}>
                            Edit rate
                          </button>
                          {c.history?.length > 0 && (
                            <button onClick={() => setExpanded(expanded === c.customer_id ? null : c.customer_id)}
                              className="text-[11px] text-gray-400 hover:text-gray-700">
                              {expanded === c.customer_id ? '▲ hide' : `▼ log (${c.history.length})`}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {expanded === c.customer_id && (c.history || []).map((h, i) => (
                    <tr key={i} className="bg-amber-50 border-b border-amber-100">
                      <td colSpan={2} className="px-4 py-2 pl-8 text-[11px] text-gray-400 italic">history</td>
                      <td className="px-4 py-2 text-[12px] font-semibold text-amber-900">₹{h.rate?.toFixed(2)}</td>
                      <td className="px-4 py-2 text-[12px] text-gray-500">{h.changed_by}</td>
                      <td className="px-4 py-2 text-[11px] text-gray-400 whitespace-nowrap">{fmtDT(h.changed_at)}</td>
                      <td className="px-4 py-2 text-[11px] text-gray-400">{h.month}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">{data.length} customers · {month}</div>
      </div>
    </div>
  );
}


